import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/api";
import ClienteShell from "@/components/ClienteShell";
import { ArrowLeft, Lock, Pencil, ArrowRight } from "lucide-react";

export default function ClienteConfirmar() {
  const { planilhaId } = useParams();
  const navigate = useNavigate();
  const [pedido, setPedido] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editaveis, setEditaveis] = useState({ endereco: "", telefone: "", complemento: "", referencia: "" });

  useEffect(() => {
    axios.get(`${API}/cliente/planilha/${planilhaId}`).catch(() => null).then((r) => {
      if (r?.data) {
        setPedido(r.data);
        setEditaveis({
          endereco: r.data.endereco || "",
          telefone: r.data.telefone || "",
          complemento: "",
          referencia: "",
        });
      }
    });
  }, [planilhaId]);

  const submit = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/cliente/confirmar-dados/${planilhaId}`, editaveis);
      toast.success(r.data.mensagem || "Dados confirmados");
      navigate(`/cliente/servico/${r.data.servico_id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ClienteShell
      step={1}
      headerRight={
        <Link to="/qrcode" className="text-sm font-semibold text-[#142766] hover:text-[#FF6700] transition-colors inline-flex items-center gap-1.5" data-testid="cliente-voltar-busca">
          <ArrowLeft size={15} /> Buscar outro pedido
        </Link>
      }
    >
      <div className="aj-fade-up">
        <div className="aj-eyebrow">Confirmação de dados</div>
        <h1 className="font-display font-extrabold text-3xl text-[#0F172A] mt-2">
          Confira e ajuste <span className="aj-accent-orange">o necessário</span>
        </h1>
        <p className="text-[#64748B] mt-3 text-sm">
          Você pode editar endereço, telefone, complemento e ponto de referência.
          CPF, produto e quantidade não podem ser alterados.
        </p>
      </div>

      <div className="mt-8 aj-card p-6 sm:p-7 aj-fade-up-1">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#142766] mb-4">
          <Lock size={13} /> Dados do pedido
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <ReadOnly label="Nome" v={pedido?.nome} />
          {pedido?.os_numero && <ReadOnly label="Número da OS" v={pedido.os_numero} mono />}
          <ReadOnly label="CPF" v={pedido?.cpf} mono />
          <ReadOnly label="Produto" v={pedido?.produto} />
          <ReadOnly label="Quantidade" v={pedido?.quantidade} />
          {pedido?.cor && <ReadOnly label="Cor" v={pedido.cor} />}
        </div>
      </div>

      <div className="mt-5 aj-card p-6 sm:p-7 aj-fade-up-2">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#E05C00] mb-4">
          <Pencil size={13} /> Dados de contato e entrega
        </div>
        <div className="space-y-4">
          <Editavel label="Telefone" v={editaveis.telefone} onChange={(v) => setEditaveis(e => ({ ...e, telefone: v }))} testid="cliente-telefone" />
          <Editavel label="Endereço" v={editaveis.endereco} onChange={(v) => setEditaveis(e => ({ ...e, endereco: v }))} testid="cliente-endereco" />
          <Editavel label="Complemento" v={editaveis.complemento} onChange={(v) => setEditaveis(e => ({ ...e, complemento: v }))} testid="cliente-complemento" />
          <Editavel label="Ponto de referência" v={editaveis.referencia} onChange={(v) => setEditaveis(e => ({ ...e, referencia: v }))} testid="cliente-referencia" />
        </div>
      </div>

      <button
        onClick={submit}
        disabled={loading}
        className="btn-aj-primary mt-8 h-12 w-full justify-center disabled:opacity-60"
        data-testid="cliente-confirmar-button"
      >
        {loading ? "Confirmando…" : "Confirmar e solicitar montagem"}
        <ArrowRight size={17} />
      </button>
      <div className="mt-4 text-xs text-[#94A3B8] text-center">
        Em até 48h você receberá uma data disponível para montagem.
      </div>
    </ClienteShell>
  );
}

function ReadOnly({ label, v, mono }) {
  return (
    <div>
      <Label className="text-[11px] font-bold uppercase tracking-wider text-[#64748B]">{label}</Label>
      <div className={`mt-1.5 px-3 py-2.5 rounded-lg border border-[#E2E8F0] bg-[#F1F5F9] text-sm text-[#334155] ${mono ? "font-mono" : ""}`}>
        {v || "—"}
      </div>
    </div>
  );
}
function Editavel({ label, v, onChange, testid }) {
  return (
    <div>
      <Label className="text-[11px] font-bold uppercase tracking-wider text-[#142766]">{label}</Label>
      <Input
        value={v}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 rounded-lg border-[#CBD5E1] focus-visible:ring-[#FF6700]"
        data-testid={testid}
      />
    </div>
  );
}
